import { useState, useCallback, useMemo } from 'react';
import {
  Stack,
  Group,
  Button,
  Title,
  Paper,
  Loader,
  Center,
} from '@mantine/core';
import { IconPlus, IconMicroscope } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Patient, Specimen } from '@medplum/fhirtypes';
import { formatHumanName } from '@medplum/core';
import { useMedplum, useResource } from '@medplum/react';
import { useNavigate } from 'react-router-dom';
import { SpecimenCreateEditDrawer } from './SpecimenCreateEditDrawer';
import * as SpecimenUtils from './specimens-table.utils';
import {
  useSpecimenSearch,
  SPECIMEN_SEARCH_DEFAULTS,
  SpecimenSearchParams,
} from '../hooks/useSpecimenSearch';
import {
  getSpecimenSearchFields,
  getSpecimenTableColumns,
} from './specimenSearchConfig';
import { ResourceSearchForm } from '@/components/resource-search/ResourceSearchForm';
import { ResourceTable } from '@/components/resource-search/ResourceTable';

interface SpecimensPageProps {
  patientId?: string;
  hideCreateButton?: boolean;
}

export function SpecimensPage({
  patientId,
  hideCreateButton = false,
}: SpecimensPageProps): JSX.Element {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const { t } = useTranslation('lims');
  const patient = useResource<Patient>(
    patientId ? { reference: `Patient/${patientId}` } : undefined
  );

  // Use the specimen search hook
  const {
    resources: specimens,
    loading,
    page,
    totalPages,
    totalCount,
    setPage,
    patients,
    searchParams,
    updateSearchParams,
    clearSearch,
    patientNameQuery,
    setPatientNameQuery,
    setPatientQuery,
    patientNameOptions,
    patientLoading,
    patientNameLoading,
    resourceIdQuery: specimenIdQuery,
    setResourceIdQuery: setSpecimenIdQuery,
    resourceIdOptions: specimenIdOptions,
    resourceIdLoading: specimenIdLoading,
    fetchResources: fetchSpecimens,
  } = useSpecimenSearch(medplum, patientId);

  // Modal state
  const [selectedSpecimen, setSelectedSpecimen] = useState<Specimen | null>(
    null
  );
  const [createEditModalOpen, setCreateEditModalOpen] = useState(false);

  // Clear search handler
  const handleSearch = useCallback(
    (updates: Partial<SpecimenSearchParams>) => {
      const merged = { ...searchParams, ...updates };
      setPage(1);
      updateSearchParams(updates);
      fetchSpecimens(merged);
    },
    [fetchSpecimens, searchParams, setPage, updateSearchParams]
  );

  const handleClearSearch = useCallback(() => {
    clearSearch(SPECIMEN_SEARCH_DEFAULTS);
  }, [clearSearch]);

  // Helper function wrappers using utility functions
  const getPatientName = useCallback(
    (specimen: Specimen): string =>
      SpecimenUtils.getPatientName(specimen, patientId, patient, patients, t),
    [patient, patientId, patients, t]
  );

  const getSpecimenId = useCallback(
    (specimen: Specimen): string => SpecimenUtils.getSpecimenId(specimen),
    []
  );

  const getSpecimenType = useCallback(
    (specimen: Specimen): string => SpecimenUtils.getSpecimenType(specimen, t),
    [t]
  );

  const getQualityName = useCallback(
    (specimen: Specimen): string => SpecimenUtils.getQualityName(specimen, t),
    [t]
  );

  // ---------------------------------------------------------------------------
  const handleCreateSpecimen = useCallback(() => {
    setSelectedSpecimen(null);
    setCreateEditModalOpen(true);
  }, []);

  const handleSpecimenCreated = () => {
    setCreateEditModalOpen(false);
    setSelectedSpecimen(null);
    fetchSpecimens(); // Refresh the list
  };

  const fields = useMemo(
    () =>
      getSpecimenSearchFields({
        t,
        searchParams,
        onSearch: handleSearch,
        hidePatientName: !!patientId,
        patientNameQuery,
        setPatientNameQuery,
        setPatientQuery,
        patientNameOptions,
        patientLoading,
        patientNameLoading,
        specimenIdQuery,
        setSpecimenIdQuery,
        specimenIdOptions,
        specimenIdLoading,
      }),
    [
      handleSearch,
      patientId,
      patientLoading,
      patientNameLoading,
      patientNameOptions,
      patientNameQuery,
      searchParams,
      setPatientNameQuery,
      setPatientQuery,
      setSpecimenIdQuery,
      specimenIdLoading,
      specimenIdOptions,
      specimenIdQuery,
      t,
    ]
  );

  const getCollectionDate = useCallback(
    (specimen: Specimen) =>
      specimen.collection?.collectedDateTime
        ? new Date(specimen.collection.collectedDateTime).toLocaleDateString()
        : t('specimensPage.notCollected', 'Not collected', { ns: 'lims' }),
    [t]
  );

  const columns = useMemo(
    () =>
      getSpecimenTableColumns({
        t,
        hidePatientColumn: !!patientId,
        getSpecimenId,
        getPatientName,
        getSpecimenType,
        getQualityName,
        getCollectionDate,
      }),
    [
      getCollectionDate,
      getPatientName,
      getQualityName,
      getSpecimenId,
      getSpecimenType,
      patientId,
      t,
    ]
  );

  const handleSpecimenClick = useCallback(
    (specimen: Specimen) => {
      if (specimen.id) {
        navigate(`/lims/specimen/${specimen.id}`);
      }
    },
    [navigate]
  );

  return (
    <Stack gap="md" className="p-6">
      <Group justify="space-between">
        <Group gap="sm">
          <IconMicroscope size={24} />
          <Title order={2}>
            {patientId
              ? t('specimensPage.patientSpecimens', {
                  patientName: formatHumanName(patient?.name?.[0]),
                })
              : t('specimensPage.allSpecimens')}
          </Title>
        </Group>
        {!hideCreateButton && (
          <Button
            variant="light"
            leftSection={<IconPlus size={16} />}
            onClick={handleCreateSpecimen}
          >
            {t('specimensPage.createSpecimen')}
          </Button>
        )}
      </Group>

      <ResourceSearchForm<SpecimenSearchParams>
        fields={fields}
        searchParams={searchParams}
        onSearch={handleSearch}
        onClear={handleClearSearch}
        clearLabel={t('specimensPage.clear', 'Clear', { ns: 'lims' })}
        layout={{ cols: { base: 1, sm: 2, lg: patientId ? 3 : 4 } }}
      />

      <Paper shadow="sm" withBorder>
        {loading ? (
          <Center p="xl">
            <Loader />
          </Center>
        ) : (
          <ResourceTable<Specimen>
            data={specimens}
            columns={columns}
            page={page}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={setPage}
            onRowClick={handleSpecimenClick}
            onActionClick={handleSpecimenClick}
            emptyText={t(
              'specimensPage.noSpecimensFound',
              'No specimens found',
              {
                ns: 'lims',
              }
            )}
          />
        )}
      </Paper>

      {/* Create/Edit Modal */}
      <SpecimenCreateEditDrawer
        opened={createEditModalOpen}
        onClose={handleSpecimenCreated}
        specimen={selectedSpecimen}
        patient={patient}
      />
    </Stack>
  );
}
